import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Mail, Clock, Users, Send, Settings2, Loader2, CalendarDays,
  CheckCircle, XCircle, History, Plus, X, Activity, DollarSign, Headphones,
} from 'lucide-react';

const REPORT_ICONS = {
  productivity: Activity,
  daily_finance: DollarSign,
  weekly_coaching: Headphones,
};

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const HOURS = Array.from({length: 24}, (_, i) => i);

export default function ReportSchedulerPage() {
  const [reportTypes, setReportTypes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(null);
  const [sendHistory, setSendHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Schedule form state
  const [form, setForm] = useState({
    enabled: true,
    frequency: 'daily',
    send_day: 'monday',
    send_hour: 9,
    send_minute: 0,
    recipients: [],
    external_emails: [],
    filters: {},
  });
  const [newExtEmail, setNewExtEmail] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [typesRes, usersRes] = await Promise.all([
        apiClient.get('/reports/types'),
        apiClient.get('/reports/recipients'),
      ]);
      setReportTypes(typesRes.data || []);
      setAllUsers(usersRes.data || []);
    } catch { toast.error('Failed to load report types'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openConfig = (rt) => {
    const sched = rt.schedule || {};
    setForm({
      enabled: sched.enabled ?? true,
      frequency: sched.frequency || (rt.supports_schedule?.[0] || 'daily'),
      send_day: sched.send_day || 'monday',
      send_hour: sched.send_hour ?? 9,
      send_minute: sched.send_minute ?? 0,
      recipients: sched.recipients || [],
      external_emails: sched.external_emails || [],
      filters: sched.filters || {},
    });
    setConfigOpen(rt.type);
    setUserSearch('');
    setNewExtEmail('');
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/reports/schedules/${configOpen}`, form);
      toast.success('Schedule saved');
      setConfigOpen(null);
      fetchData();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const sendTest = async (type) => {
    setSendingTest(type);
    try {
      const res = await apiClient.post(`/reports/send-test/${type}`);
      if (res.data?.sent) toast.success(`Test report sent to ${res.data.to}`);
      else toast.error(res.data?.error || 'Failed to send');
    } catch { toast.error('Failed to send test'); }
    setSendingTest(null);
  };

  const toggleRecipient = (user) => {
    setForm(prev => {
      const exists = prev.recipients.find(r => r.user_id === user.id);
      if (exists) return { ...prev, recipients: prev.recipients.filter(r => r.user_id !== user.id) };
      return { ...prev, recipients: [...prev.recipients, { user_id: user.id, email: user.email, name: user.full_name }] };
    });
  };

  const addExternalEmail = () => {
    const email = newExtEmail.trim();
    if (!email || !email.includes('@')) return;
    setForm(prev => ({ ...prev, external_emails: [...prev.external_emails, email] }));
    setNewExtEmail('');
  };

  const removeExtEmail = (email) => {
    setForm(prev => ({ ...prev, external_emails: prev.external_emails.filter(e => e !== email) }));
  };

  const loadHistory = async () => {
    try {
      const res = await apiClient.get('/reports/send-history?limit=30');
      setSendHistory(res.data || []);
      setHistoryOpen(true);
    } catch { toast.error('Failed to load history'); }
  };

  const filteredUsers = userSearch
    ? allUsers.filter(u => u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()))
    : allUsers;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="report-scheduler-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Report Scheduler</h1>
          <p className="text-muted-foreground text-sm">Configure automated reports and manage recipients</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadHistory} data-testid="send-history-btn">
          <History className="h-4 w-4 mr-1" /> Send History
        </Button>
      </div>

      {/* Report Type Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {reportTypes.map(rt => {
          const Icon = REPORT_ICONS[rt.type] || Mail;
          const recipientCount = rt.schedule?.recipients?.length || 0;
          const extCount = rt.schedule?.external_emails?.length || 0;

          return (
            <Card key={rt.type} className={`transition-all ${rt.is_scheduled ? 'border-emerald-500/40' : ''}`}
              data-testid={`report-card-${rt.type}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${rt.is_scheduled ? 'bg-emerald-500/10' : 'bg-muted'}`}>
                      <Icon className={`h-5 w-5 ${rt.is_scheduled ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-sm">{rt.name}</CardTitle>
                      <CardDescription className="text-xs">{rt.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {rt.is_scheduled ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                      <span className="text-xs text-emerald-600 font-medium">Active</span>
                      <Badge variant="secondary" className="text-[10px] ml-auto">
                        {rt.schedule.frequency} @ {String(rt.schedule.send_hour).padStart(2,'0')}:{String(rt.schedule.send_minute || 0).padStart(2,'0')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{recipientCount + extCount} recipient{recipientCount + extCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Not scheduled</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => openConfig(rt)}
                    data-testid={`configure-${rt.type}`}>
                    <Settings2 className="h-3.5 w-3.5 mr-1" /> Configure
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs"
                    disabled={sendingTest === rt.type}
                    onClick={() => sendTest(rt.type)}
                    data-testid={`test-${rt.type}`}>
                    {sendingTest === rt.type ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                    Test
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Configure Dialog */}
      <Dialog open={!!configOpen} onOpenChange={() => setConfigOpen(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Report Schedule</DialogTitle>
            <DialogDescription>Set frequency, time, and recipients for this report</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Enable / Disable */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Enable Auto-Send</Label>
              <Switch checked={form.enabled} onCheckedChange={v => setForm(p => ({...p, enabled: v}))}
                data-testid="schedule-enabled-switch" />
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Frequency</Label>
              <Select value={form.frequency} onValueChange={v => setForm(p => ({...p, frequency: v}))}>
                <SelectTrigger className="h-9" data-testid="frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly (1st of month)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Day (for weekly) */}
            {form.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label className="text-xs font-medium">Day of Week</Label>
                <Select value={form.send_day} onValueChange={v => setForm(p => ({...p, send_day: v}))}>
                  <SelectTrigger className="h-9" data-testid="send-day-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map(d => <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Time */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Send Time (UAE)</Label>
              <div className="flex items-center gap-2">
                <Select value={String(form.send_hour)} onValueChange={v => setForm(p => ({...p, send_hour: Number(v)}))}>
                  <SelectTrigger className="h-9 w-24" data-testid="send-hour-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map(h => <SelectItem key={h} value={String(h)}>{String(h).padStart(2,'0')}:00</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">UAE Time (GMT+4)</span>
              </div>
            </div>

            {/* Date Range Filter (for productivity) */}
            {configOpen === 'productivity' && (
              <div className="space-y-2">
                <Label className="text-xs font-medium">Report Period</Label>
                <Select value={form.filters.date_range || 'this_month'} onValueChange={v => setForm(p => ({...p, filters: {...p.filters, date_range: v}}))}>
                  <SelectTrigger className="h-9" data-testid="date-range-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today Only</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Recipients */}
            <div className="space-y-3">
              <Label className="text-xs font-medium">Recipients ({form.recipients.length} selected)</Label>
              <Input placeholder="Search people..." value={userSearch} onChange={e => setUserSearch(e.target.value)}
                className="h-8 text-xs" data-testid="recipient-search" />
              <ScrollArea className="h-[180px] border rounded-md p-2">
                {filteredUsers.map(u => {
                  const isSelected = form.recipients.some(r => r.user_id === u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 py-1.5 px-1 text-xs cursor-pointer hover:bg-muted/50 rounded">
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleRecipient(u)} />
                      <span className="flex-1">{u.full_name}</span>
                      <span className="text-muted-foreground text-[10px]">{u.role?.replace(/_/g,' ')}</span>
                    </label>
                  );
                })}
              </ScrollArea>
            </div>

            {/* External Emails */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">External Emails</Label>
              <div className="flex gap-2">
                <Input placeholder="email@example.com" value={newExtEmail} onChange={e => setNewExtEmail(e.target.value)}
                  className="h-8 text-xs flex-1" data-testid="external-email-input"
                  onKeyDown={e => e.key === 'Enter' && addExternalEmail()} />
                <Button size="sm" variant="outline" onClick={addExternalEmail} data-testid="add-ext-email-btn">
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.external_emails.map(em => (
                  <Badge key={em} variant="secondary" className="text-[10px] gap-1">
                    {em}
                    <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => removeExtEmail(em)} />
                  </Badge>
                ))}
              </div>
            </div>

            <Button onClick={saveSchedule} disabled={saving} className="w-full" data-testid="save-schedule-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Save Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send History</DialogTitle>
            <DialogDescription>Recent report emails sent</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            {sendHistory.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">No reports sent yet</p>
            ) : (
              <div className="space-y-2">
                {sendHistory.map((h, i) => (
                  <div key={i} className="border rounded-lg p-3 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-[10px]">{h.report_type}</Badge>
                      <span className="text-muted-foreground">{new Date(h.sent_at).toLocaleString()}</span>
                    </div>
                    <div className="text-muted-foreground">
                      Sent to: {h.recipients?.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
